import { AssetBalance } from '@/types/balances';
import { RefObject, useState } from 'react';
import { useGetCryptoDepositAddressQuery, useGetWhitelistQuery } from '@/features/crypto/cryptoApi';
import { selectIsLoggedIn } from '@/features/oAuth/oAuthSlice';
import InfoBox from '@/shared/components/InfoBox/InfoBox';
import { OrderType } from '@/shared/constants/commonTypes';
import { isRestrictedAsset } from '@/shared/helpers/assets';
import { useClipboard } from '@/shared/hooks/clipboard';
import { normalizedBig } from '@cex/shared-modules';
import { IonIcon, IonText } from '@ionic/react';
import { alertCircleOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CryptoDeposit from './CryptoDeposit/CryptoDeposit';
import CryptoWithdrawal from './CryptoWithdrawal/CryptoWithdrawal';
import styles from './DepositWithdraw.module.css';
import CryptoWithdrawalModal from '@/shared/components/CryptoWithdrawalModal/CryptoWithdrawalModal';
import { Address } from '@/types';

type Props = {
  assetBalance: AssetBalance | undefined;
  withdrawCryptoChallengeData?: any;
  isOpen: boolean;
  modal: RefObject<HTMLIonModalElement>;
  setIsOpen: (value: boolean) => void;
  handleInitTravelRule?: (value: boolean, withdrawCryptoData: any) => void;
};

const DepositWithdrawContent: React.FC<Props> = ({
  assetBalance,
  withdrawCryptoChallengeData,
  isOpen,
  modal,
  setIsOpen,
  handleInitTravelRule
}) => {
  const [formType, setFormType] = useState<string>('wallet.deposit');
  const [orderType, setOrderType] = useState(OrderType.Market);
  const [targetAddress, setTargetAddress] = useState<Address | null>(null);
  const { t } = useTranslation();

  const isLoggedIn = useSelector(selectIsLoggedIn);

  const clipboard = useClipboard();

  const assetId = assetBalance?.assetId || '';

  const { data: cryptoData, isLoading: cryptoLoading } = useGetCryptoDepositAddressQuery(assetId, {
    refetchOnMountOrArgChange: true,
    skip: !isLoggedIn || assetId === 'eur'
  });

  const available = normalizedBig(assetBalance?.available);

  const { data: whitelist } = useGetWhitelistQuery();

  const handleCopy = () => {
    clipboard.copy(cryptoData?.address || '', `${t('settings.address')} copied`);
  };

  const dismissModal = () => {
    setIsOpen(false);
    modal.current?.dismiss();
  };

  return (
    <InfoBox
      toggleSwitch={{
        labels: ['wallet.deposit', 'wallet.withdraw']
      }}
      withDropdown={false}
      orderType={orderType}
      setOrderType={setOrderType}
      formType={formType}
      setFormType={setFormType}
    >
      {formType === 'wallet.deposit' &&
        (isRestrictedAsset(assetId) ? (
          <div className={styles.depositWarning}>
            <IonIcon color="warning" icon={alertCircleOutline}></IonIcon>
            <h3>{t('error.deposite.crypto.title')}</h3>
            <IonText>{t('error.deposite.crypto.body')}</IonText>
          </div>
        ) : (
          <CryptoDeposit
            assetId={assetId}
            cryptoData={cryptoData}
            cryptoLoading={cryptoLoading}
            handleCopy={handleCopy}
          />
        ))}
      {formType === 'wallet.withdraw' && (
        <CryptoWithdrawal
          assetId={assetId}
          availableAssetQuantity={available}
          dismissModal={dismissModal}
          isOpen={isOpen}
          modal={modal}
          assetBalance={assetBalance!}
          whitelistedAddresses={whitelist?.filter((whiteListItem) => whiteListItem.assetId === assetId)}
          setIsOpen={setIsOpen}
          handleInitTravelRule={handleInitTravelRule}
          targetAddress={targetAddress}
          setTargetAddress={setTargetAddress}
        />
      )}

      {withdrawCryptoChallengeData && (
        <CryptoWithdrawalModal
          dismissModal={dismissModal}
          modal={modal}
          withdrawalData={withdrawCryptoChallengeData}
          isOpen={isOpen}
          targetAddress={targetAddress!}
        />
      )}
    </InfoBox>
  );
};

const DepositWithdraw: React.FC<Props> = ({
  assetBalance,
  withdrawCryptoChallengeData,
  isOpen,
  modal,
  setIsOpen,
  handleInitTravelRule
}) => {
  if (!assetBalance) {
    return null;
  }

  return (
    <DepositWithdrawContent
      handleInitTravelRule={handleInitTravelRule}
      withdrawCryptoChallengeData={withdrawCryptoChallengeData}
      modal={modal}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      assetBalance={assetBalance}
    />
  );
};

export default DepositWithdraw;
